import ReactDOM from "react-dom";
import React, { useState } from "react";

import * as _ from "lodash";

import { Formik, Field, Form, ErrorMessage } from "formik";

import { UiUtils } from "../../common/uiUtils";
import { ViewerUtils } from "../../common/viewerUtils";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions, viewerState } from "../../viewer";
import * as SmartDrawings from "../../smartDrawings";
import { Misc } from "../../common/misc";
import { QuickFilterUtils } from "./quickFilterUtils";
import { FormField } from "../../common/forms";

interface EditQuickFilterProps {
    name: string;
    text: string;
    submit: (name: string, text: string) => void;
}

interface QuickFilterListProps {
    filters: SmartDrawings.QuickFilter[];
    selectedFilterId: string;
    addFilter: () => void;
    removeFilter: () => void;
    removeAllFilters: () => void;
    selectFilter: (id) => void;
    viewer: Autodesk.Viewing.GuiViewer3D;
}

interface QuickFilterProps {
    filter: SmartDrawings.QuickFilter;
    selected: boolean;
    onClick: () => void;
    onSelect: (params: string) => void;
    onIsolate: (params: string) => void;
}

interface QuickFilterUiProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
}

interface QuickFilterUiState {
    filters: { [key: string]: SmartDrawings.QuickFilter; };
    selectedFilterId: string;
}

const CommentField = (props) => {
    return <div className="form-group row">
               <label className="col-formlabel col-sm-3">{props.label}</label>
               <div className="col">
                   <Field type={props.type} component={props.component} name={props.name} className="form-control" rows={props.rows}/>
                   <ErrorMessage name={props.name} component="div"/>
               </div>
           </div>;
}

const EditForm: React.SFC<EditQuickFilterProps> = (props) => {
    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                name: props.name,
                text: props.text
            }}
            onSubmit={(values, actions) => {
                actions.setSubmitting(false);
                props.submit(values.name, values.text);
            }}
            validate={(values: any) => {
                const errors: any = {};

                if (!values.name) {
                    errors.name = "Required";
                }

                if (!values.text) {
                    errors.text = "Required";
                }

                return errors;
            }}
            render={({ values, errors, status, touched, isSubmitting, handleReset, setValues }) => (
                <Form className="container pt-3" style={{ width: "auto" }}>

                    <FormField name="name" label="Name *" />
                    <CommentField type="text" name="text" label="Filter rule *" component="textarea" rows={3}/>

                    <div className="form-group row">
                        <div className="col">
                            <span>* Required fields</span>
                        </div>
                    </div>

                    <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                        Update
                    </button>
                </Form>
            )}/>
    );
}

const QuickFilterRow: React.SFC<QuickFilterProps> = props => {

    return <li className="comment-list-item" style={props.selected ? { border: "solid white" } : null} onClick={props.onClick}>
        <div className="comment-list-label">{props.filter.name || "<No name>"}</div>
               <div className="btn-group"><button className="btn btn-success btn-sm" onClick={() => props.onSelect(props.filter.text)}>
            Select
        </button>
            <button className="btn btn-primary btn-sm" onClick={() => props.onIsolate(props.filter.text)}>
                Isolate
            </button></div>
           </li>;

}

const QuickFilterList: React.SFC<QuickFilterListProps> = (props) => {
    return <div>
               <ul className="plain-list" style={{ height: "150px", overflow: "hidden", overflowY: "scroll" }}>
                   {props.filters.map(c => {
                       return <QuickFilterRow
                           key={c.id}
                           filter={c}
                           selected={c.id === props.selectedFilterId}
                           onClick={() => props.selectFilter(c.id)}
                           onSelect={params => QuickFilterUtils.selectWithFilterString(params, props.viewer, false)}
                           onIsolate={params => QuickFilterUtils.isolateWithFilterString(params, props.viewer, false)}/>;
                   })}
               </ul>
               <button className="btn btn-primary" onClick={props.addFilter}>
                   Add
               </button>
        <button className="btn btn-primary ml-1" onClick={props.removeFilter} disabled={props.selectedFilterId === null}>
                   Remove
               </button>
        <button className="btn btn-primary ml-1" onClick={props.removeAllFilters} disabled={_.isEmpty(props.filters)}>
            Remove all
               </button>
        <button className="btn btn-success ml-5" onClick={() => {
            props.viewer.clearSelection;
            props.viewer.showAll();
        }}>
                   Clear filter
               </button>
           </div>;
}

class QuickFilterUi extends React.Component<QuickFilterUiProps, QuickFilterUiState> {

    constructor(props) {
        super(props);
        this.state = {
            filters: {},
            selectedFilterId: null
        }
    }

    componentDidMount(): void {
        this.removeAllFilters();
        if (!_.isEmpty(viewerState.filters)) {
            this.setState({ filters: viewerState.filters });
            this.selectFilter(_.first(_.values(viewerState.filters)).id);
        };
    }

    componentWillUnmount(): void {
        this.selectFilter(null);
    }

    componentDidUpdate(prevProps: {readonly [P in "viewer"]: QuickFilterUiProps[P]},
        prevState: {readonly [P in "filters" | "selectedFilterId"]: QuickFilterUiState[P]},
        snapshot?): void {
        viewerState.filters = this.state.filters;
    }

    selectFilter = (id) => {
        this.setState({ selectedFilterId: id });
    }

    addFilter = () => {
        const id = Misc.guid();
        const updatedFilters = { ...this.state.filters };
        updatedFilters[id] = { id: id, name: "", text: "" };
        this.setState({ filters: updatedFilters });
        this.selectFilter(id);
    }

    removeFilter = () => {
        const updatedFilters = { ...this.state.filters };
        delete updatedFilters[this.state.selectedFilterId];
        this.setState({ filters: updatedFilters, selectedFilterId: null });
    }

    removeAllFilters = () => {
        this.setState({ filters: {} });
    }

    updateFilter = (name, text) => {
        const selectedId = this.state.selectedFilterId;
        const updatedFilters = { ...this.state.filters };
        const filter = updatedFilters[selectedId];
        filter.name = name || filter.name;
        filter.text = text || filter.text;
        updatedFilters[selectedId] = filter;
        this.setState({ filters: updatedFilters });
    }

    render() {
        const filters = _.values(this.state.filters);
        const selectedFilter = this.state.filters[this.state.selectedFilterId];
        return <div id="comment-react" className="react-content">
            <QuickFilterList
                viewer={this.props.viewer}
                filters={filters}
                                selectedFilterId={this.state.selectedFilterId}
                addFilter={this.addFilter}
                                removeFilter={this.removeFilter}
                removeAllFilters={this.removeAllFilters}
                                selectFilter={this.selectFilter}/>
                   {!_.isEmpty(this.state.filters) &&
                <EditForm name={selectedFilter != null ? selectedFilter.name : ""}
                    text={selectedFilter != null ? selectedFilter.text : ""} submit={(name, text) => this
                           .updateFilter(name, text)}/> }
               </div>;
    }

}

class QuickFilterPanel extends Autodesk.Viewing.UI.DockingPanel {

    private viewer: Autodesk.Viewing.GuiViewer3D;

    private contentDiv: HTMLDivElement;
    private reactNode;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, container: any, id: string) {
        super(container, id, "Quick filters");
        this.viewer = viewer;

        this.container.classList.add("comment-panel");

        this.contentDiv = document.createElement("div");
        this.contentDiv.classList.add("content");
        this.container.appendChild(this.contentDiv);
    }

    setVisible(show: boolean) {
        super.setVisible(show);
        if (show) {
            ReactDOM.unmountComponentAtNode(this.contentDiv);
            this.reactNode = ReactDOM.render(<QuickFilterUi viewer={this.viewer} />, this.contentDiv);
        } else if (this.reactNode) {
            ReactDOM.unmountComponentAtNode(this.contentDiv);
            this.reactNode = null;
        }
    }
}

export { QuickFilterUi, QuickFilterPanel };